import {
  ClockSlide,
  GroupRefSlide,
  ImageSlide,
  QrSlide,
  ReviewSlide,
  Slide,
  TeamSlide,
  TextSlide,
  VideoSlide,
  VimeoSlide,
  YouTubeSlide,
} from "@shared/types";
import { normalizeSlide } from "@shared/playlist";
import { MediaUploadResult } from "./api";
import { formatLongDate, nextBusinessDay, shiftDate, usHolidays } from "./holidays";

function uid(): string {
  return crypto.randomUUID();
}

const INCOMPLETE_REASON: Partial<Record<Slide["type"], string>> = {
  youtube: "no YouTube link set",
  vimeo: "no Vimeo link set",
  qr: "no link/text for the QR code",
};

/** Slides the server would silently DROP on save (they fail normalization —
 *  e.g. a YouTube slide without a video). Returns human-readable labels so the
 *  editor can warn instead of losing the admin's work. */
export function findIncompleteSlides(slides: Slide[], defaultDuration: number): string[] {
  const problems: string[] = [];
  slides.forEach((s, i) => {
    if (s.type === "groupref") return;
    if (normalizeSlide(s, defaultDuration) === null) {
      const reason = INCOMPLETE_REASON[s.type] ?? "missing required info";
      problems.push(`slide ${i + 1} (${s.type}: ${reason})`);
    }
  });
  return problems;
}

export interface HolidaySlideOptions {
  /** Holiday names (as in usHolidays) the clinic closes for. */
  selected: string[];
  /** Shown as "For urgent needs, call …" when set. */
  phone?: string;
  colors?: { bgColor: string; textColor: string };
}

/** Generate scheduled "we're closed" text slides for the chosen holidays of a
 *  year. Each slide runs for the week leading up to (and including) the
 *  observed day off, and names the next day the office is open — skipping
 *  weekends and the other selected closures. */
export function makeHolidaySlides(
  year: number,
  defaultDuration: number,
  opts: HolidaySlideOptions,
): TextSlide[] {
  const wanted = new Set(opts.selected);
  const chosen = usHolidays(year)
    .filter((h) => wanted.has(h.name))
    .sort((a, b) => a.observed.localeCompare(b.observed));
  const closed = new Set(chosen.map((h) => h.observed));
  const phone = opts.phone?.trim();
  // Two holidays can land on the same day (Good Friday and Prince Kūhiō Day
  // in some years) — one "Closed" slide per day, naming both.
  const byDate = new Map<string, string[]>();
  for (const h of chosen) byDate.set(h.observed, [...(byDate.get(h.observed) ?? []), h.name]);
  return [...byDate].map(([observed, names]) => {
    const lines = [
      `In observance of ${names.join(" and ")}.`,
      `We reopen ${formatLongDate(nextBusinessDay(observed, closed))}.`,
    ];
    if (phone) lines.push(`For urgent needs, call ${phone}.`);
    return {
      id: uid(),
      type: "text",
      enabled: true,
      durationSec: defaultDuration,
      group: `Holidays ${year}`,
      title: `Closed ${formatLongDate(observed)}`,
      body: lines.join("\n"),
      bgColor: opts.colors?.bgColor ?? "#0f3d4c",
      textColor: opts.colors?.textColor ?? "#ffffff",
      align: "center",
      schedule: { startDate: shiftDate(observed, -7), endDate: observed },
    };
  });
}

export function makeTextSlide(
  defaultDuration: number,
  defaults?: { bgColor: string; textColor: string },
): TextSlide {
  return {
    id: uid(),
    type: "text",
    enabled: true,
    durationSec: defaultDuration,
    title: "New text slide",
    body: "",
    bgColor: defaults?.bgColor ?? "#0b1120",
    textColor: defaults?.textColor ?? "#f8fafc",
    align: "center",
  };
}

export function makeImageSlide(media: MediaUploadResult, defaultDuration: number): ImageSlide {
  return {
    id: uid(),
    type: "image",
    enabled: true,
    durationSec: defaultDuration,
    mediaKey: media.key,
    fileName: media.fileName,
    fit: "contain",
    rotation: 0,
  };
}

export function makeVideoSlide(media: MediaUploadResult, defaultDuration: number): VideoSlide {
  return {
    id: uid(),
    type: "video",
    enabled: true,
    durationSec: defaultDuration,
    mediaKey: media.key,
    fileName: media.fileName,
    fit: "contain",
    rotation: 0,
    audio: false,
    loop: false,
    captions: "auto",
  };
}

export function makeGroupRefSlide(sharedGroupId: string, name: string): GroupRefSlide {
  return { id: uid(), type: "groupref", enabled: true, sharedGroupId, name };
}

export function makeClockSlide(defaultDuration: number): ClockSlide {
  return {
    id: uid(),
    type: "clock",
    enabled: true,
    durationSec: defaultDuration,
    showDate: true,
    showSeconds: false,
    hour24: false,
    showWeather: false,
    tempUnit: "F",
    bgColor: "#0b1120",
    textColor: "#f8fafc",
  };
}

export function makeYouTubeSlide(videoId: string, defaultDuration: number, title?: string): YouTubeSlide {
  return { id: uid(), type: "youtube", enabled: true, durationSec: defaultDuration, videoId, title, loop: false };
}

export function makeVimeoSlide(videoId: string, defaultDuration: number, hash?: string): VimeoSlide {
  return { id: uid(), type: "vimeo", enabled: true, durationSec: defaultDuration, videoId, hash, loop: false };
}

export function makeTeamSlide(defaultDuration: number): TeamSlide {
  return {
    id: uid(),
    type: "team",
    enabled: true,
    durationSec: defaultDuration,
    name: "Dr. Jane Smith",
    role: "Family Physician",
    bio: "Caring for our community for over 10 years.",
    bgColor: "#0b1120",
    textColor: "#f8fafc",
  };
}

export function makeQrSlide(defaultDuration: number): QrSlide {
  return {
    id: uid(),
    type: "qr",
    enabled: true,
    durationSec: defaultDuration,
    data: "",
    title: "Scan to leave us a review",
    caption: "Point your phone camera at the code",
    bgColor: "#ffffff",
    fgColor: "#0b1120",
  };
}

export function makeReviewSlide(defaultDuration: number): ReviewSlide {
  return {
    id: uid(),
    type: "review",
    enabled: true,
    durationSec: defaultDuration,
    quote: "We had a wonderful experience — friendly staff and no wait!",
    author: "A happy patient",
    rating: 5,
    source: "google",
    bgColor: "#0b1120",
    textColor: "#f8fafc",
  };
}

export function slideFromUpload(media: MediaUploadResult, defaultDuration: number): Slide | null {
  if (media.contentType.startsWith("image/")) return makeImageSlide(media, defaultDuration);
  if (media.contentType.startsWith("video/")) return makeVideoSlide(media, defaultDuration);
  return null;
}

/** A stable key identifying which group a slide belongs to (ungrouped slides
 *  are each their own group). Used by the expand viewer's up/down navigation. */
export function groupKey(slide: Slide, index: number): string {
  if (slide.type === "groupref") return `ref:${slide.id}`;
  return slide.group ? `grp:${slide.group}` : `solo:${index}`;
}

/** Indices that start a new group, in order. */
export function groupBoundaries(slides: Slide[]): number[] {
  const boundaries: number[] = [];
  let prev: string | null = null;
  slides.forEach((s, i) => {
    const k = groupKey(s, i);
    if (k !== prev) boundaries.push(i);
    prev = k;
  });
  return boundaries;
}

/** Jump to the start of the next (dir=1) or previous (dir=-1) group. */
export function jumpGroup(slides: Slide[], current: number, dir: 1 | -1): number {
  const boundaries = groupBoundaries(slides);
  if (dir === 1) {
    const next = boundaries.find((b) => b > current);
    return next ?? current;
  }
  const prevs = boundaries.filter((b) => b < current);
  return prevs.length ? prevs[prevs.length - 1] : current;
}

export function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (to < 0 || to >= arr.length || from === to) return arr;
  const copy = [...arr];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
}
